<script setup lang="ts">
import CalculationSideBar from '@/components/calculation-form/CalculationSideBar.vue'
import { useCalculationStore } from '@/stores/calculation'
import { storeToRefs } from 'pinia'
import { computed, markRaw, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import type {
  CalculationFormTab,
  CalculationFormTabs,
  FormRefs
} from '@/components/calculation-form/type/type'
import UpdateProjectParamsDialog from '@/components/calculation-form/UpdateProjectParamsDialog.vue'
import GeneralForm from '@/components/calculation-form/GeneralForm.vue'
import VesselLoadCaseForm from '@/components/calculation-form/VesselLoadCaseForm.vue'
import EnvironmentLoadCaseForm from '@/components/calculation-form/EnvironmentLoadCaseForm.vue'
import StandardParameterForm from '@/components/calculation-form/StandardParameterForm.vue'
import { STATUS_CHOICES } from '@/components/calculation-form/constants/constants'

const route = useRoute()

const calculationStore = useCalculationStore()
const { currentItem: currentCalculationItem } = storeToRefs(calculationStore)

const formRefs = ref<FormRefs>([])
const tab = ref<CalculationFormTab>('GENERAL')
const tabs = ref<CalculationFormTabs>([
  {
    value: 'GENERAL',
    label: 'General',
    isCompleted: false,
    isShown: true,
    component: markRaw(GeneralForm)
  },
  {
    value: 'VESSEL_LOAD_CASE',
    label: 'Vessel load case',
    isCompleted: false,
    isShown: true,
    component: markRaw(VesselLoadCaseForm)
  },
  {
    value: 'ENVIRONMENT_LOAD_CASE',
    label: 'Environment load case',
    isCompleted: false,
    isShown: true,
    component: markRaw(EnvironmentLoadCaseForm)
  },
  {
    value: 'STANDARD_PARAMETERS',
    label: 'Standard parameters',
    isCompleted: false,
    isShown: true,
    component: markRaw(StandardParameterForm)
  }
])

const updateTabValue = (value: CalculationFormTab) => {
  tab.value = value
}

const updateTabCompletion = (value: CalculationFormTab, isCompleted: boolean) => {
  tabs.value.find((tab) => tab.value === value)!.isCompleted = isCompleted
}

const checkProjectParams = async () => {
  if (!currentCalculationItem.value) return
  if (await calculationStore.checkProjectParams(currentCalculationItem.value)) return
  calculationStore.openUpdateProjectParamsDialog()
}

const resetInput = async () => {
  if (!currentCalculationItem.value) return
  const { project, id } = currentCalculationItem.value
  const initialItem = { ...calculationStore.createNewCalculation(project), id, name: '-' }
  calculationStore.setCurrentItem(initialItem)
  await calculationStore.update(initialItem)

  for (const refItem of formRefs.value) {
    await refItem.formRef.validate()
    refItem.formRef.resetValidation()
  }
  tab.value = 'GENERAL'
}

onMounted(async () => {
  if (!currentCalculationItem.value) {
    await calculationStore.fetchOptions()
    const calculationId = route.params.calculationId as string
    await calculationStore.fetchItem(parseInt(calculationId))
  }
  if (!isReadOnly.value) {
    await checkProjectParams()
  }
})

const isReadOnly = computed(() => {
  return (
    currentCalculationItem.value?.status !== STATUS_CHOICES.DRAFT ||
    !calculationStore.canChangeCalculation
  )
})
</script>

<template>
  <CalculationSideBar :tab="tab" :tabs="tabs" @update-tab-value="updateTabValue" />

  <v-window v-model="tab" class="px-8 py-6">
    <v-window-item v-for="item in tabs" :key="item.value" :value="item.value">
      <component
        ref="formRefs"
        v-if="currentCalculationItem"
        :is="item.component"
        :tab="item.value"
        :disabled="false"
        :readonly="isReadOnly"
        @update-tab-completion="updateTabCompletion"
      >
        <template #resetInputButton>
          <v-btn
            v-if="!isReadOnly"
            class="text-none my-1 mx-2"
            variant="tonal"
            prepend-icon="mdi-restart"
            @click.stop="resetInput"
          >
            Reset input
          </v-btn>
        </template>
      </component>
      <UpdateProjectParamsDialog />
    </v-window-item>
  </v-window>
</template>

<style scoped></style>
